<template>
  <div class="d-flex">
    <ScheduleXCalendar
      v-resize="onResize"
      :calendar-app="calendarApp"
      :custom-components="customComponents"
    />
  </div>
  <DialogVacation />
  <DialogTimeSlot />
  <ProfilAddressAlert v-if="dialogStore.dialogYouNeedProfilesAndAddress" />
</template>

<script lang="ts" setup>
import '@holitime/schedule-x-theme-default/dist/index.css'
import { ScheduleXCalendar } from '@holitime/schedule-x-vue'
import '@holitime/schedule-x-theme-default/dist/date-picker.css'
import { calendarApp } from '@/components/calendar/calendarApp'
import { CustomComponents } from '@holitime/schedule-x-shared'
import CustomSidebarWrapper from '@/components/calendar/CustomSidebarWrapper.vue'
import { useDisplay } from 'vuetify'
import { useSidebarState } from '@/components/calendar/calendarState'
import DialogVacation from '@/components/calendar/DialogVacation.vue'
import { onMounted, ref, nextTick, onUnmounted, watch } from 'vue'
import { useTimeSlotStore } from '@/store/timeslot/timeSlotStore'
import EventBus from '@/utils/eventBus'
import { useAppointmentReasonStore } from '@/store/appointment-reason/appointementReasonStore'
import { InitTimeSlotParams } from '@/store/timeslot/timeSlotEvent'
import CustomTimeGridEvent from '@/components/calendar/CustomTimeGridEvent.vue'
import { useAddressStore } from '@/store/address/addressStore'
import DialogTimeSlot from '@/components/calendar/DialogTimeSlot.vue'
import CustomMonthGridEvent from '@/components/calendar/CustomMonthGridEvent.vue'
import CustomMonthAgenda from '@/components/calendar/CustomMonthAgenda.vue'
import { useI18n } from 'vue-i18n'
import { useProfileStore } from '@/store/profile/profileStore'
import { useDialogTimeSlotStore } from '@/store/timeslot/dialogTimeSlotStore'
import CustomVacationBtn from '@/components/calendar/CustomVacationBtn.vue'
import { useRoute, onBeforeRouteLeave } from 'vue-router'
import ProfilAddressAlert from '@/components/dialog/ProfilAddressAlert.vue'
import { useOnboardingStore } from '@/store/onboarding/onboardingStore'

const { t } = useI18n()
const { initTimeSlots } = useTimeSlotStore()
const appointmentReasonStore = useAppointmentReasonStore()
const addressStore = useAddressStore()
const dialogStore = useDialogTimeSlotStore()
const route = useRoute()

onUnmounted(() => {
  dialogStore.dialogYouNeedProfilesAndAddress = false
})

EventBus.on(
  'initTimeSlots',
  (
    {
      profileIds,
      appointmentReasonIds,
      addressIds,
      ownerId,
      start,
      end,
    }: InitTimeSlotParams = {
      profileIds: profileStore.profiles.map(p => p.id),
      appointmentReasonIds: appointmentReasonStore.appointmentReasons.map(
        a => a.id,
      ),
      addressIds: addressStore.addresses.map(a => a.id),
      start: null,
      end: null,
    },
  ) => {
    initTimeSlots({
      profileIds,
      appointmentReasonIds,
      addressIds,
      ownerId,
      start,
      end,
    })
  },
)

const dialogYouNeedProfilesAndAddress = ref(false)

const openDialogYouNeedProfilesAndAddress = () => {
  dialogYouNeedProfilesAndAddress.value = true
}

const profileStore = useProfileStore()

onMounted(async () => {
  nextTick(() => {
    const el = document.querySelector(
      '.sx__calendar-header-content-add-time-off',
    )
    if (el) {
      el.setAttribute('style', 'display: none !important')
    }
  })
  await profileStore.initProfiles()
  await addressStore.initAddresses()
  initTimeSlots()
  await profileStore.refreshProfiles()
  await addressStore.refreshAddresses()
  await dialogStore.resetDialogTimeSlotCreationProfileAddress()
  await appointmentReasonStore.refreshAppointmentReasons()
})

const customComponents = ref<CustomComponents>({
  // headerContentRightAppend: CustomVacationBtn as unknown as any,
  sidebar: CustomSidebarWrapper as unknown as any,
  timeGridEvent: CustomTimeGridEvent as unknown as any,
  monthGridEvent: CustomMonthGridEvent as unknown as any,
  monthAgendaEvent: CustomMonthAgenda as unknown as any,
})

const { lgAndUp, xs } = useDisplay()
const { sidebarState } = useSidebarState()

const onResize = () => {
  const calendarContainer = document.querySelector('.sx__calendar-content')
  if (!calendarContainer) return
  if (lgAndUp.value && sidebarState.value) {
    calendarContainer.classList.remove('hidden-sidebar')
  }
  if (
    lgAndUp.value &&
    calendarContainer.classList.contains('hidden-sidebar') &&
    !sidebarState.value
  ) {
    calendarContainer.classList.add('hidden-sidebar')
  }
  if (!lgAndUp.value) {
    calendarContainer.classList.remove('hidden-sidebar')
  }
}

onBeforeRouteLeave((to, from, next) => {
  const onboarding = useOnboardingStore()
  if (onboarding.hasOnboarding && !onboarding.manualProccess) {
    onboarding.onboardingAlert = true
    onboarding.leave = false

    const unwatch = watch(
      () => onboarding.leave,
      confirmed => {
        if (confirmed) {
          onboarding.leave = false
          onboarding.onboardingAlert = false
          next()
        } else {
          onboarding.onboardingAlert = false
          next(false)
        }
        unwatch()
      },
    )
  } else {
    next()
  }
})
</script>

<style lang="scss" scoped>
@import url('./styleCalendar.css');

.page-header .title {
  font-size: 33px;
  line-height: 46.2px;
  font-weight: 700;
  color: #004838;
}
.page-header .breadcrumb {
  list-style: none;
}

.page-meta .icon {
  width: 16px;
  height: 16px;
}

.page-header .breadcrumb .icon {
  width: 16px;
  height: 16px;
  flex-basis: 16px;
}
.page-header .breadcrumb li {
  font-size: 14px;
  color: #9e9e9e;
}
.page-header .breadcrumb li:not(:last-child) {
  margin-right: 10px;
}
.page-header .breadcrumb li:last-child {
  color: #004838;
  font-weight: 500;
  size: 14px;
  text-decoration: underline;
}

.affiliate-data {
  font-size: 14px;
  line-height: 19.6px;
  font-weight: 500;
  color: #9e9e9e;
  gap: 8px;
  justify-items: end;
}

.aff-link {
  padding: 10px 16px 10px 16px;
  background: #12c29b;
  border-radius: 40px;
  color: #fff;
  cursor: pointer;
  line-height: 16.8px;
  order: 2;
}
.aff-link .home-link {
  margin-right: 10px;
}

.follow {
  list-style: none;
  font-size: 14px;
  line-height: 19.6px;
  font-weight: 500;
  color: #9e9e9e;
  gap: 8px;
}

.follow .icon {
  cursor: pointer;
}

.affi-status {
  margin-top: 12px;
}

.affi-status-head {
  gap: 12px;
}

.rounded-bg {
  background: rgba(18, 194, 155, 0.2);
  height: 31px;
  width: 31px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 40px;
}

.control-btn {
  padding: 4px 16px;
  gap: 10px;
  border: 1px solid rgba(224, 224, 224, 1);
  border-radius: 40px;
  display: flex;
  align-items: center;
  line-height: 21px;
  font-size: 14px;
  min-height: 36px;
  color: rgba(158, 158, 158, 1);
  cursor: pointer;
}

.v-menu > .v-overlay__content > .v-card,
.v-menu > .v-overlay__content > .v-sheet,
.v-menu > .v-overlay__content > .v-list {
  background: white !important;
}

// .timeSlotDialogModal > .v-overlay__content {
//   width: max-content !important;
//   max-width: max-content !important;
// }
</style>
